'use client';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SSOSignUpError } from '@sso/auth-client';
import { clientSignUp } from '@sso/auth-client/client';

import { useGtmAuthEvents } from '@workspace/gtm/nextjs';
import { useAuth } from '@workspace/lib/auth/auth-provider';
import { SSO_AUTH_CONFIG } from '@workspace/lib/auth/config';
import { useIdentity } from '@workspace/lib/hooks/analytics/use-identity';
import {
  SESSION_STORE_KEYS,
  SessionData,
  useSessionStore,
} from '@workspace/lib/hooks/store/use-session-store';
import type { FormInputs } from '@workspace/lib/types/form-schema';
import { composeIdentity } from '@workspace/lib/utils/analytics/compose-identity';
import { transformFormDataForSubmission } from '@workspace/lib/utils/form-fields-mapping';
import { getPhoneCountryPrefix } from '@workspace/lib/utils/get-phone-country-prefix';
import { isRecaptchaEnabled } from '@workspace/lib/utils/recaptcha-config';

export { clientRefreshToken, clientSignUp } from '@sso/auth-client/client';

// x-recaptcha-token is the header the SSO portal's verifyRecaptcha reads.
// Not exported from the bundled @sso/auth-client tgz (SSO-portal-internal
// constant), so mirrored here. See CST-25639.
const X_RECAPTCHA_TOKEN = 'x-recaptcha-token';

// clientSignUp accepts SignUpOptions from the bundled @sso/auth-client tgz,
// which does not expose extraHeaders publicly. baseSignUp (called internally)
// does accept it via destructuring. Use a narrow local type to pass the header
// without requiring a new tgz release. See CST-25639.
type SignUpWithHeaders = Parameters<typeof clientSignUp>[0] & {
  extraHeaders?: Record<string, string>;
};

const SSO_BODY_TEMPLATE = {
  firstName: '{{firstName|to_original}}',
  lastName: '{{lastName|to_original}}',
  phoneNumber: '{{mobilePhoneNumber|to_phone_number}}',
  pin: '{{pin|to_original}}',
  emailAddress: '{{emailAddress|to_original}}',
};

// Sign-up has no idempotency key, so only a request that never reached the
// portal is retried — anything else could register a duplicate user.
const SIGN_UP_RETRY_DELAYS_MS = [500, 1500];
const SIGN_UP_ATTEMPTS = SIGN_UP_RETRY_DELAYS_MS.length + 1;

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// Check once at module init — stable for the lifetime of the app.
const recaptchaEnabled = isRecaptchaEnabled();

export const useSilentSignUp = () => {
  // Only call useGoogleReCaptcha when the provider is mounted (recaptcha enabled).
  // When disabled, the provider is not in the tree and this hook would throw.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { executeRecaptcha } = recaptchaEnabled
    ? useGoogleReCaptcha()
    : { executeRecaptcha: undefined };

  const { saveToSessionStore, getFromSessionStore } = useSessionStore();
  const { setIdentity } = useIdentity();
  const { fireLogin } = useGtmAuthEvents();
  const { user } = useAuth();

  const attemptSignUp = async (data: FormInputs): Promise<boolean> => {
    const { firstName, lastName, phoneNumber, pin, emailAddress } =
      transformFormDataForSubmission(SSO_BODY_TEMPLATE, data) as Record<
        string,
        string
      >;

    // Already signed in as this user: a second sign-up would only conflict.
    // Matched on email so another account's session cannot pass as this user.
    if (
      user?.email &&
      user.email.toLowerCase() === emailAddress.trim().toLowerCase()
    ) {
      return true;
    }

    const phonePrefix = getPhoneCountryPrefix();
    if (!phonePrefix) {
      throw new Error(
        'Unable to resolve phone country prefix — check NEXT_PUBLIC_SITE_LOCALE.',
      );
    }

    const sessionData = getFromSessionStore(
      SESSION_STORE_KEYS.sessionData,
    ) as SessionData;
    const applicationId =
      typeof sessionData?.applicationId === 'string'
        ? sessionData.applicationId
        : undefined;

    const body = {
      firstName,
      lastName,
      phoneNumber: `${phonePrefix}${phoneNumber}`,
      identities: [
        { signInType: 'PIN' as const, issuerAssignedId: pin },
        { signInType: 'EMAIL' as const, issuerAssignedId: emailAddress },
      ],
      ...(applicationId ? { applicationId } : {}),
    };

    // NEXT_PUBLIC_ENABLE_GOOGLE_RECAPTCHA controls whether reCAPTCHA is active.
    // When disabled, skip reCAPTCHA entirely and proceed without the header.
    // When enabled, NEXT_PUBLIC_RECAPTCHA_SITE_KEY must be set and the
    // provider must have initialised — otherwise bail early.
    if (recaptchaEnabled && !executeRecaptcha) {
      // If the reCAPTCHA provider hasn't initialised, bail early — sending the
      // request without a token guarantees RECAPTCHA_INTERNAL from the SSO portal.
      console.warn(
        '[useSilentSignUp] executeRecaptcha not available — GoogleReCaptchaProvider may not be mounted or the reCAPTCHA script has not loaded yet.',
      );
      return false;
    }

    // Note: declared outside the loop so TypeScript doesn't narrow it to
    // undefined inside the spread below, but assigned fresh on every attempt
    // because reCAPTCHA tokens are single-use: reusing a token that was
    // already consumed by SSO (even if the response was lost) will cause the
    // retry to be rejected as duplicate/expired.
    let recaptchaToken: string | undefined;

    for (let attempt = 1; attempt <= SIGN_UP_ATTEMPTS; attempt++) {
      let response: Response;
      try {
        if (recaptchaEnabled) {
          recaptchaToken = await executeRecaptcha!('silent_sign_up');
          if (!recaptchaToken) return false;
        }

        response = await (
          clientSignUp as (
            args: SignUpWithHeaders,
          ) => ReturnType<typeof clientSignUp>
        )({
          authConfig: SSO_AUTH_CONFIG,
          body,
          silentSignIn: true,
          ...(recaptchaToken && {
            extraHeaders: { [X_RECAPTCHA_TOKEN]: recaptchaToken },
          }),
        });
      } catch (error) {
        // The portal answered, so retrying would duplicate the user.
        if (error instanceof SSOSignUpError) return false;

        if (attempt === SIGN_UP_ATTEMPTS) return false;

        await wait(SIGN_UP_RETRY_DELAYS_MS[attempt - 1]!);
        continue;
      }

      // The portal answered, so retrying would duplicate the user.
      let result;

      try {
        result = await response.json();
      } catch {
        return false;
      }

      // A 2xx without a user means no session cookies were set.
      if (!result?.success || !result.user) return false;

      try {
        const identityObj = await composeIdentity({
          isAuthenticated: true,
          applicationType: sessionData?.applicationType,
          email: emailAddress,
          phone: phoneNumber,
        });

        setIdentity(identityObj);
        fireLogin('email', identityObj);
        saveToSessionStore(SESSION_STORE_KEYS.isLoginEventFired, true);
      } catch {
        // The session is established: analytics must not block the user.
      }

      return true;
    }

    return false;
  };

  /**
   * Ensures the user has an SSO session. Never throws: on `false` callers must
   * not navigate to a protected route, or middleware bounces them to login.
   */
  const silentSignUp = async (data: FormInputs): Promise<boolean> => {
    try {
      return await attemptSignUp(data);
    } catch {
      return false;
    }
  };

  return { silentSignUp };
};
